import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Me,
  Tickets,
  Users,
  type TicketSummary,
  type TicketStatus,
  type TicketPriority,
  type TicketArea,
  type TicketSortField,
  type TicketView,
  type UserSummary,
} from '../lib/api';
import { useTicketEvents } from '../lib/events';
import { StatusBadge, ListStatusBadge, PriorityBadge, RankBadge, TicketTags } from '../components/Badge';
import { priorityHasBadge, statusLabel, isDueToday } from '../lib/ticket-ui';
import { promptDialog, alertDialog } from '../components/ui/confirm';
import { Dialog } from '../components/ui/Dialog';
import { UserPicker } from '../components/UserPicker';

const STATUSES: (TicketStatus | '')[] = [
  '',
  'OPEN',
  'IN_PROGRESS',
  'BLOCKED',
  'PENDING_CODE_REVIEW',
  'READY_TO_DEPLOY',
  'PENDING_CUSTOMER_REVIEW',
  'PENDING_REPORTER_REVIEW',
  'PENDING_VERIFICATION',
  'FUTURE',
  'RESOLVED',
];
const PRIORITIES: (TicketPriority | '')[] = ['', 'LOW', 'NORMAL', 'HIGH', 'URGENT'];
const AREAS: (TicketArea | '')[] = ['', 'CUSTOMER_WEBSITE', 'PRODUCTION', 'OTHER'];

const PAGE_SIZE = 50;

const SORT_LABELS: Record<TicketSortField, string> = {
  id: '#',
  title: 'Title',
  status: 'Status',
  priority: 'Priority',
  area: 'Area',
  isCustomerService: 'Customer service',
  rank: 'Rank',
  updatedAt: 'Updated',
  createdAt: 'Created',
  assignee: 'Assignee',
  priorityStale: 'Priority, stalest first',
};

// The list filters survive leaving the page and carry into new tabs (tickets
// open in their own tab, and its "Tickets" link should show the same list).
const FILTERS_KEY = 'ticketList.filters';
interface SavedFilters {
  view: TicketView;
  status: TicketStatus | '';
  priority: TicketPriority | '';
  area: TicketArea | '';
  q: string;
  sort: TicketSortField;
  order: 'asc' | 'desc';
}
function loadSavedFilters(): Partial<SavedFilters> {
  try {
    return JSON.parse(localStorage.getItem(FILTERS_KEY) ?? '{}') as Partial<SavedFilters>;
  } catch {
    return {};
  }
}

// "If created today, show only the time; otherwise show the date."
// Format a YYYY-MM-DD due date as "Jul 17". Parse the parts into a LOCAL date so
// the day never shifts (new Date('2026-07-17') would be UTC midnight = the prior
// evening in Mountain time).
function fmtDue(ymd: string): string {
  const parts = ymd.slice(0, 10).split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtCreated(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
      });
}

// Compact relative age ("now", "5m", "3h", "2d", "4mo", "1y") for the single
// time column. The exact created/updated timestamps live in the cell's title.
function fmtRelative(iso: string): string {
  const diffMin = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const h = Math.round(diffMin / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.round(mo / 12)}y`;
}

export function TicketListPage() {
  const location = useLocation();
  // Clicking "Tickets" in the top nav passes refreshList: it drops the search
  // text but keeps the other filters.
  const refreshRequested = (location.state as { refreshList?: boolean } | null)?.refreshList === true;
  const [saved] = useState(loadSavedFilters);
  const [view, setView] = useState<TicketView>(saved.view ?? 'active');
  const [status, setStatus] = useState<TicketStatus | ''>(saved.status ?? '');
  const [priority, setPriority] = useState<TicketPriority | ''>(saved.priority ?? '');
  const [area, setArea] = useState<TicketArea | ''>(saved.area ?? '');
  const [q, setQ] = useState(refreshRequested ? '' : saved.q ?? '');
  const [sort, setSort] = useState<TicketSortField>(saved.sort ?? 'rank');
  const [order, setOrder] = useState<'asc' | 'desc'>(saved.order ?? 'desc');
  const [page, setPage] = useState(1);
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<UserSummary | null>(null);

  // Full user list is only needed for the admin "Assign to…" picker.
  const [users, setUsers] = useState<UserSummary[]>([]);

  useEffect(() => {
    Me.get().then((r) => setMe(r.user)).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (me?.role !== 'ADMIN') return;
    Users.list().then((r) => setUsers(r.users)).catch(() => undefined);
  }, [me?.role]);

  async function load(search = q) {
    setLoading(true);
    setError(null);
    try {
      const res = await Tickets.list({ view, status, priority, area, q: search, sort, order, page, limit: PAGE_SIZE });
      setTickets(res.tickets);
      setTotal(res.total);
      setTotalPages(res.totalPages);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, status, priority, area, sort, order, page]);

  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify({ view, status, priority, area, q, sort, order }));
    } catch {
      // Storage unavailable (private mode etc.) — filters just won't persist.
    }
  }, [view, status, priority, area, q, sort, order]);

  // Re-clicking "Tickets" while already on the list doesn't remount the page,
  // but it does produce a new location key: clear the search and reload.
  const firstLocationKey = useRef(location.key);
  useEffect(() => {
    if (location.key === firstLocationKey.current || !refreshRequested) return;
    setQ('');
    if (page === 1) void load('');
    else setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useTicketEvents(() => {
    void load();
  });

  function toggleSort(field: TicketSortField) {
    if (sort === field) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      // numbers and dates default to desc; text defaults to asc.
      const textFields: TicketSortField[] = ['title', 'status', 'priority', 'area'];
      setOrder(textFields.includes(field) ? 'asc' : 'desc');
    }
    setPage(1);
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-slate-500 mb-1">View</label>
          <select
            value={view}
            onChange={(e) => { setView(e.target.value as TicketView); setPage(1); }}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
          >
            <option value="all">All tickets</option>
            <option value="active">Active (not resolved or future)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Status</label>
          <select
            value={status}
            disabled={view === 'active'}
            onChange={(e) => { setStatus(e.target.value as TicketStatus | ''); setPage(1); }}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white disabled:bg-slate-100 disabled:text-slate-400"
            title={view === 'active' ? 'Status is forced to "not Resolved or Future" in this view' : undefined}
          >
            {STATUSES.map((o) => (
              <option key={o} value={o}>{o === '' ? 'Any' : statusLabel[o]}</option>
            ))}
          </select>
        </div>
        <Filter label="Priority" value={priority} onChange={(v: TicketPriority | '') => { setPriority(v); setPage(1); }} options={PRIORITIES} />
        <Filter label="Area" value={area} onChange={(v: TicketArea | '') => { setArea(v); setPage(1); }} options={AREAS} />
        <div className="hidden md:block">
          <label className="block text-xs text-slate-500 mb-1">Sort</label>
          <select
            value={sort === 'priorityStale' ? 'priorityStale' : ''}
            onChange={(e) => {
              setSort(e.target.value === 'priorityStale' ? 'priorityStale' : 'rank');
              setOrder('desc');
              setPage(1);
            }}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            title="Column headers also sort the list"
          >
            <option value="">{sort === 'rank' ? 'Rank (default)' : `By column: ${SORT_LABELS[sort]}`}</option>
            <option value="priorityStale">{SORT_LABELS.priorityStale}</option>
          </select>
        </div>
        <div className="flex-1 min-w-[150px]">
          <label className="block text-xs text-slate-500 mb-1">Search</label>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (setPage(1), load())}
            placeholder="Ticket #, title, customer, …"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
          />
        </div>
        <button
          onClick={() => { setPage(1); void load(); }}
          className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800"
        >
          Search
        </button>
      </div>

      {/* Mobile sort dropdown */}
      <div className="md:hidden">
        <label className="block text-xs text-slate-500 mb-1">Sort by</label>
        <div className="flex gap-2">
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value as TicketSortField); setPage(1); }}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
          >
            {Object.entries(SORT_LABELS).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
          <button
            onClick={() => { setOrder(o => o === 'asc' ? 'desc' : 'asc'); setPage(1); }}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            title="Toggle direction"
          >
            {order === 'asc' ? '↑ A→Z' : '↓ Z→A'}
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Desktop: table */}
      <div className="hidden md:block bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <Th field="id" sort={sort} order={order} onClick={toggleSort} className="w-16">#</Th>
              <Th field="title" sort={sort} order={order} onClick={toggleSort}>Title</Th>
              <Th field="status" sort={sort} order={order} onClick={toggleSort} className="w-36">Status</Th>
              <Th field="priority" sort={sort} order={order} onClick={toggleSort} className="w-32">Priority</Th>
              <th className="px-3 py-2 w-24">Due</th>
              <Th field="assignee" sort={sort} order={order} onClick={toggleSort} className="w-36">Assignee</Th>
              <Th field="updatedAt" sort={sort} order={order} onClick={toggleSort} className="w-20">Updated</Th>
              <th className="px-2 py-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {loading && tickets.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && tickets.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No tickets match.</td></tr>
            )}
            {tickets.map((t) => {
              const mine = !!me && t.assigneeUserId === me.id;
              return (
              <tr
                key={t.id}
                className={`border-t border-slate-100 hover:bg-slate-50 border-l-2 ${
                  mine ? 'border-l-brand-500' : 'border-l-transparent'
                }`}
              >
                <td className="px-3 py-2 text-slate-500 tabular-nums whitespace-nowrap">
                  <Link to={`/tickets/${t.id}`} target="_blank" rel="noopener" className="hover:text-brand-600">
                    #{t.id}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Link
                      to={`/tickets/${t.id}`}
                      target="_blank"
                      rel="noopener"
                      className="text-slate-900 hover:text-brand-600 font-medium"
                      title={mine ? 'Assigned to you' : undefined}
                    >
                      {t.title}
                    </Link>
                    <TicketTags area={t.area} isCustomerService={t.isCustomerService} />
                  </div>
                </td>
                <td className="px-3 py-2"><ListStatusBadge status={t.status} blockedReason={t.blockedReason} /></td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1">
                    {t.rank != null && <RankBadge rank={t.rank} />}
                    {priorityHasBadge(t.priority) && <PriorityBadge priority={t.priority} />}
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                  {t.dueDate ? (
                    <span className={isDueToday(t.dueDate) ? 'text-rose-600 font-semibold' : 'text-slate-600'}>
                      {fmtDue(t.dueDate)}
                      {t.rush && <span className="text-rose-600 font-semibold"> · Rush</span>}
                    </span>
                  ) : t.rush ? (
                    <span className="text-rose-600 font-semibold">Rush</span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {t.assignee?.name ?? (
                    <span className="text-slate-500">
                      Unassigned
                      {me && (
                        <button
                          type="button"
                          onClick={() => void assignToMe(t, me, load)}
                          className="ml-2 text-xs text-brand-600 hover:text-brand-700 hover:underline"
                        >
                          Assign me
                        </button>
                      )}
                    </span>
                  )}
                </td>
                <td
                  className="px-3 py-2 text-slate-500 whitespace-nowrap tabular-nums"
                  title={`Created ${fmtCreated(t.createdAt)} · Updated ${fmtCreated(t.updatedAt)}`}
                >
                  {fmtRelative(t.updatedAt)}
                </td>
                <td className="px-1 py-2 text-right">
                  <RowMenu ticket={t} me={me} users={users} onChanged={load} />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: cards */}
      <div className="md:hidden space-y-2">
        {loading && tickets.length === 0 && (
          <p className="text-center py-8 text-slate-500 text-sm">Loading…</p>
        )}
        {!loading && tickets.length === 0 && (
          <p className="text-center py-8 text-slate-500 text-sm">No tickets match.</p>
        )}
        {tickets.map((t) => {
          const mine = !!me && t.assigneeUserId === me.id;
          return (
          <div
            key={t.id}
            className={`border border-slate-200 rounded-lg p-3 relative bg-white hover:bg-slate-50 ${
              mine ? 'border-l-2 border-l-brand-500' : ''
            }`}
          >
            <div className="absolute top-2 right-2">
              <RowMenu ticket={t} me={me} users={users} onChanged={load} />
            </div>
            <Link to={`/tickets/${t.id}`} target="_blank" rel="noopener" className="block">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-xs text-slate-500">
                #{t.id}
                {mine && <span className="ml-1.5 text-brand-600 font-medium" title="Assigned to you">· yours</span>}
              </span>
              <span
                className="text-xs text-slate-500 tabular-nums"
                title={`Created ${fmtCreated(t.createdAt)} · Updated ${fmtCreated(t.updatedAt)}`}
              >
                {fmtRelative(t.updatedAt)}
              </span>
            </div>
            <div className="font-medium text-slate-900 break-words">
              {t.title}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <StatusBadge status={t.status} />
              {t.rank != null && <RankBadge rank={t.rank} />}
              {priorityHasBadge(t.priority) && <PriorityBadge priority={t.priority} />}
              <TicketTags area={t.area} isCustomerService={t.isCustomerService} />
              {t.dueDate && (
                <span
                  className={`text-xs ${isDueToday(t.dueDate) ? 'text-rose-600 font-semibold' : 'text-slate-500'}`}
                >
                  Due {fmtDue(t.dueDate)}
                </span>
              )}
              {t.rush && <span className="text-xs font-semibold text-rose-600">Rush</span>}
              <span className="text-xs text-slate-500">→ {t.assignee?.name ?? 'unassigned'}</span>
            </div>
            {/* Touch has no hover, so the blocked reason is shown inline on cards. */}
            {t.status === 'BLOCKED' && t.blockedReason?.trim() && (
              <p className="mt-1.5 text-xs text-rose-700 line-clamp-2 break-words">
                Blocked: {t.blockedReason.trim()}
              </p>
            )}
            </Link>
          </div>
          );
        })}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>
            {(page - 1) * PAGE_SIZE + 1}
            {'–'}
            {Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-2 py-1 rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-50"
            >
              «
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-50"
            >
              Prev
            </button>
            <span className="px-2 text-xs text-slate-500">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-50"
            >
              Next
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              className="px-2 py-1 rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-50"
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Th({
  field,
  sort,
  order,
  onClick,
  className,
  children,
}: {
  field: TicketSortField;
  sort: TicketSortField;
  order: 'asc' | 'desc';
  onClick: (field: TicketSortField) => void;
  className?: string;
  children: React.ReactNode;
}) {
  const active = sort === field;
  return (
    <th
      aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`px-3 py-2 ${className ?? ''}`}
    >
      <button
        type="button"
        onClick={() => onClick(field)}
        className="inline-flex items-center gap-1 select-none rounded hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        {children}
        <span className={`text-xs ${active ? 'text-slate-700' : 'text-slate-500'}`} aria-hidden="true">
          {active ? (order === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  );
}

/**
 * Assign a ticket to the current user. Unassigned tickets go through the
 * atomic /claim endpoint (409 if someone else got there first); taking over a
 * ticket already assigned to someone else is a self-assign PATCH, which the
 * API allows for developers and admins.
 */
async function assignToMe(ticket: TicketSummary, me: UserSummary, onChanged: () => void) {
  try {
    if (ticket.assigneeUserId) {
      await Tickets.patch(ticket.id, { assigneeUserId: me.id });
    } else {
      await Tickets.claim(ticket.id);
    }
  } catch (e) {
    await alertDialog({
      title: 'Could not assign ticket',
      body: e instanceof Error ? e.message : 'Assignment failed',
    });
  }
  onChanged();
}

function RowMenu({
  ticket,
  me,
  users,
  onChanged,
}: {
  ticket: TicketSummary;
  me: UserSummary | null;
  users: UserSummary[];
  onChanged: () => void;
}) {
  const ticketId = ticket.id;
  const [open, setOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigneeDraft, setAssigneeDraft] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);

  const isMine = !!me && ticket.assigneeUserId === me.id;
  // STAFF can only claim unassigned tickets; reassigning needs PATCH rights.
  const canAssignToMe = !!me && !isMine && (!ticket.assigneeUserId || me.role !== 'STAFF');
  // Assigning someone else is admin-only (enforced by the API too).
  const canAssignOthers = me?.role === 'ADMIN';

  function openAssign() {
    setOpen(false);
    setAssigneeDraft(ticket.assigneeUserId);
    setAssignOpen(true);
  }

  async function saveAssign() {
    setAssigning(true);
    try {
      await Tickets.patch(ticketId, { assigneeUserId: assigneeDraft });
      setAssignOpen(false);
      onChanged();
    } catch (e) {
      await alertDialog({
        title: 'Could not assign ticket',
        body: e instanceof Error ? e.message : 'Assignment failed',
      });
    } finally {
      setAssigning(false);
    }
  }
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  async function markDuplicate() {
    setOpen(false);
    const ans = await promptDialog({
      title: 'Mark as duplicate',
      label: `Which ticket is #${ticketId} a duplicate of? Enter the original's number.`,
      placeholder: 'e.g. 123',
      required: true,
    });
    if (ans === null) return;
    const target = Number(ans.replace(/^#/, '').trim());
    if (!Number.isFinite(target) || target <= 0) {
      await alertDialog({ title: 'Invalid ticket number', body: 'Please enter a valid ticket number.' });
      return;
    }
    if (target === ticketId) {
      await alertDialog({ title: 'Cannot link', body: 'A ticket cannot be a duplicate of itself.' });
      return;
    }
    try {
      await Tickets.addLink(ticketId, target, 'DUPLICATE_OF');
      onChanged();
    } catch (e) {
      await alertDialog({
        title: 'Failed to mark as duplicate',
        body: e instanceof Error ? e.message : 'Failed to mark as duplicate',
      });
    }
  }

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
        className="px-2 py-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded text-base leading-none"
        aria-label="Ticket actions"
      >
        ⋯
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-10 bg-white border border-slate-200 rounded-md shadow-lg w-44">
          {canAssignToMe && me && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
                void assignToMe(ticket, me, onChanged);
              }}
              className="block w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Assign to me
            </button>
          )}
          {canAssignOthers && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAssign(); }}
              className="block w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Assign to…
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); void markDuplicate(); }}
            className="block w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Mark as duplicate…
          </button>
        </div>
      )}
      {canAssignOthers && (
        <Dialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          title={`Assign #${ticketId}`}
          description={ticket.title}
          footer={
            <>
              <button
                type="button"
                onClick={() => setAssignOpen(false)}
                className="px-3 py-1.5 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void saveAssign()}
                disabled={assigning || assigneeDraft === ticket.assigneeUserId}
                className="px-3 py-1.5 rounded-md bg-brand-600 text-white text-sm hover:bg-brand-700 disabled:opacity-60"
              >
                {assigning ? 'Saving…' : 'Assign'}
              </button>
            </>
          }
        >
          <div className="min-h-64 text-left">
            <UserPicker
              users={users}
              value={assigneeDraft}
              onChange={setAssigneeDraft}
              allowEmpty
              emptyLabel="Unassigned"
            />
          </div>
        </Dialog>
      )}
    </div>
  );
}

function Filter<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: T[];
}) {
  return (
    <div>
      <label className="block text-xs text-slate-500 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
      >
        {options.map((o) => (
          <option key={o} value={o}>{o === '' ? 'Any' : o.replace(/_/g, ' ')}</option>
        ))}
      </select>
    </div>
  );
}
